import {
  Badge,
  Box,
  Button,
  Center,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useColorModeValue,
  useToast,
  VStack,
  HStack,
  Spinner,
} from '@chakra-ui/react';
import useAuth, { setAuthStorage } from 'contexts/useAuth';
import { useState, useEffect } from 'react';
import { MdSwapHoriz, MdExpandMore, MdSearch } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { queryClient } from 'index';
import { DateTime } from 'luxon';
import api, { getAccountsAccess, changeUserAccount } from 'services/api.service';
import { maskAccountNumber } from 'utils/account';

interface AccountAccess {
  customerAccess: {
    id: string;
    name: string;
    email: string;
    accountNumber: string;
  };
}

const normalizeText = (value = '') =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

export default function AccountSwitcher() {
  const { userData, updateUserData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountAccess[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [search, setSearch] = useState('');

  const textColor = useColorModeValue('gray.200', 'gray.200');
  const menuBg = useColorModeValue('brand.900', 'navy.800');
  const hoverBg = useColorModeValue('brand.700', 'navy.700');
  const inputBg = useColorModeValue('whiteAlpha.100', 'whiteAlpha.100');

  const currentAccount = accounts.find((account) => account.customerAccess.id === userData?.id);
  const switchableAccounts = currentAccount
    ? accounts.filter((account) => account.customerAccess.id !== currentAccount.customerAccess.id)
    : accounts;
  const normalizedSearch = normalizeText(search.trim());
  const normalizedAccountSearch = search.replace(/\D/g, '');
  const filteredAccounts = switchableAccounts.filter((account) => {
    if (!normalizedSearch) {
      return true;
    }

    const nameMatches = normalizeText(account.customerAccess.name).includes(normalizedSearch);
    const emailMatches = normalizeText(account.customerAccess.email).includes(normalizedSearch);
    const accountMatches = normalizedAccountSearch
      ? account.customerAccess.accountNumber.replace(/\D/g, '').includes(normalizedAccountSearch)
      : false;

    return nameMatches || emailMatches || accountMatches;
  });

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      setLoading(true);
      const data = await getAccountsAccess();
      setAccounts(data);
    } catch (error) {
      // Silently fail - user may not have access to multiple accounts
      console.error('Error loading accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitchAccount = async (accountId: string) => {
    try {
      setLoadingAccount(true);
      const response = await changeUserAccount(accountId);

      // 1. Update localStorage with new token
      localStorage.setItem('Authorization', response.token);

      // 2. Update axios default headers with new token
      api.defaults.headers.Authorization = 'Bearer ' + response.token;

      // 3. Update auth storage with new user data
      const newUserData = {
        authorization: response.token,
        email: response.email,
        isAuth: true,
        id: response.id,
        name: response.name,
        balance: response.balance,
        amountLocked: response.amountLocked,
        balancePending: response.balancePending,
        features: response.features,
        accountNumber: response.accountNumber,
        on: DateTime.now().toISO(),
      };
      setAuthStorage(newUserData);

      // 4. Invalidate all queries to refetch with new account data
      await queryClient.invalidateQueries();

      // 5. Update user data in context
      updateUserData();

      toast({
        title: 'Sucesso',
        description: `Conta alterada para ${response.name}`,
        status: 'success',
        duration: 3000,
        position: 'top',
        isClosable: true,
      });

      // 6. Redirect to dashboard
      navigate('/access/dashboard');
      
      // 7. Force page reload to ensure all components are updated
      window.location.reload();
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.message || 'Erro ao trocar de conta',
        status: 'error',
        duration: 5000,
        position: 'top',
        isClosable: true,
      });
    } finally {
      setLoadingAccount(false);
    }
  };

  // Don't show if no accounts available
  if (!loading && switchableAccounts.length === 0) {
    return null;
  }

  return (
    <Box w="100%" px={4} mb={4}>
      <Menu onClose={() => setSearch('')}>
        <MenuButton
          as={Button}
          w="100%"
          variant="ghost"
          color={textColor}
          rightIcon={loading ? <Spinner size="sm" /> : <Icon as={MdExpandMore} />}
          leftIcon={<Icon as={MdSwapHoriz} />}
          _hover={{ bg: hoverBg }}
          _active={{ bg: hoverBg }}
          isDisabled={loading || loadingAccount}
          size="sm"
        >
          <VStack spacing={0} align="start" flex={1}>
            <Text fontSize="xs" opacity={0.7}>
              Trocar de conta
            </Text>
            <Text fontSize="sm" fontWeight="bold" isTruncated maxW="150px">
              {userData?.name || 'Carregando...'}
            </Text>
          </VStack>
        </MenuButton>
        <Portal>
          <MenuList
            bg={menuBg}
            borderColor="whiteAlpha.300"
            minW="280px"
            maxW="320px"
            maxH="70vh"
            p={0}
            overflow="hidden"
            display="flex"
            flexDirection="column"
            zIndex="popover"
          >
            <Box px={3} py={2} borderBottomWidth={1} borderColor="whiteAlpha.300">
              <Text fontSize="xs" color={textColor} opacity={0.7} fontWeight="semibold">
                Contas disponíveis
              </Text>
            </Box>

            <Box
              px={3}
              py={3}
              borderBottomWidth={1}
              borderColor="whiteAlpha.300"
              onClick={(event) => event.stopPropagation()}
            >
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => event.stopPropagation()}
                  placeholder="Buscar cliente, email ou conta"
                  bg={inputBg}
                  color={textColor}
                  borderColor="whiteAlpha.300"
                  _hover={{ borderColor: 'whiteAlpha.400' }}
                  _focus={{
                    borderColor: 'whiteAlpha.500',
                    boxShadow: '0 0 0 1px rgba(255, 255, 255, 0.28)',
                  }}
                  _placeholder={{ color: 'gray.400' }}
                />
              </InputGroup>
            </Box>

            {currentAccount && (
              <Box px={3} py={3} borderBottomWidth={1} borderColor="whiteAlpha.300">
                <Text fontSize="xs" color={textColor} opacity={0.7} fontWeight="semibold" mb={2}>
                  Minha conta
                </Text>
                <Box bg="whiteAlpha.100" borderWidth={1} borderColor="whiteAlpha.300" borderRadius="md" px={3} py={3}>
                  <HStack justify="space-between" spacing={3} align="start">
                    <VStack align="start" spacing={1} flex={1} minW={0}>
                      <Text color={textColor} fontSize="sm" fontWeight="bold" isTruncated w="100%">
                        {currentAccount.customerAccess.name}
                      </Text>
                      <Text color={textColor} fontSize="xs" opacity={0.7} isTruncated w="100%">
                        {currentAccount.customerAccess.email}
                      </Text>
                      <Text color={textColor} fontSize="xs" opacity={0.8} fontWeight="medium">
                        Conta: {maskAccountNumber(currentAccount.customerAccess.accountNumber)}
                      </Text>
                    </VStack>
                    <Badge colorScheme="green" variant="subtle" mt={0.5}>
                      Atual
                    </Badge>
                  </HStack>
                </Box>
              </Box>
            )}

            <Box overflowY="auto" flex={1} minH={0}>
              {loading && (
                <Center px={3} py={4}>
                  <Spinner size="sm" color={textColor} />
                </Center>
              )}

              {!loading &&
                filteredAccounts.map((account) => (
                  <MenuItem
                    key={account.customerAccess.id}
                    onClick={() => handleSwitchAccount(account.customerAccess.id)}
                    bg={menuBg}
                    _hover={{ bg: hoverBg }}
                    _focus={{ bg: hoverBg }}
                    isDisabled={loadingAccount}
                    py={3}
                    px={3}
                  >
                    <HStack w="100%" justify="space-between" spacing={3}>
                      <VStack align="start" spacing={1} flex={1} minW={0}>
                        <Text color={textColor} fontSize="sm" fontWeight="bold" isTruncated w="100%">
                          {account.customerAccess.name}
                        </Text>
                        <Text color={textColor} fontSize="xs" opacity={0.7} isTruncated w="100%">
                          {account.customerAccess.email}
                        </Text>
                        <Text color={textColor} fontSize="xs" opacity={0.8} fontWeight="medium">
                          Conta: {maskAccountNumber(account.customerAccess.accountNumber)}
                        </Text>
                      </VStack>
                      {loadingAccount && <Spinner size="xs" color={textColor} />}
                    </HStack>
                  </MenuItem>
                ))}

              {!loading && filteredAccounts.length === 0 && (
                <Box px={3} py={4}>
                  <Text fontSize="sm" color={textColor} opacity={0.7}>
                    Nenhuma conta encontrada
                  </Text>
                </Box>
              )}
            </Box>
          </MenuList>
        </Portal>
      </Menu>
    </Box>
  );
}
